import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { checkoutAdminAPI, validationAPI } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import {
  downloadMassiveInpasExport,
  InpasPresenceValidation,
  inpasMonthNames,
} from '../utils/inpasExport';
import { downloadMassiveStandardExport } from '../utils/standardTimesheetExport';

const TimesheetsMassExport: React.FC = () => {
  const { isSuperAdmin, isGroupAdmin } = usePermissions();
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const includeProvisional = false;
  const [loading, setLoading] = useState<boolean>(false);
  const [exportingInpas, setExportingInpas] = useState<boolean>(false);
  const [exportingStandard, setExportingStandard] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [stats, setStats] = useState<{
    total: number;
    regular: number;
    withAnomalies: number;
  } | null>(null);
  const [validatedEmployees, setValidatedEmployees] = useState<InpasPresenceValidation[]>([]);

  useEffect(() => {
    loadStats();
  }, [year, month]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const allValidations = await validationAPI.getValidationsForAdmin({
        year,
        month,
      });

      const regular = await validationAPI.getValidationsForAdmin({
        year,
        month,
        has_anomalies: false,
      });

      setValidatedEmployees(allValidations.validations || []);
      setStats({
        total: allValidations.count || 0,
        regular: regular.count || 0,
        withAnomalies: (allValidations.count || 0) - (regular.count || 0),
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nel caricamento statistiche');
      setValidatedEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRegularValidations = async () => {
    const response = await validationAPI.getRegularValidationsForExport(
      year,
      month,
      includeProvisional
    );

    return (response.validations || []) as InpasPresenceValidation[];
  };

  const handleExportInpas = async () => {
    try {
      setExportingInpas(true);
      setError('');
      setSuccess('');

      const validations = await loadRegularValidations();

      if (validations.length === 0) {
        setError('Nessun cartellino regolare trovato per il periodo selezionato');
        return;
      }

      await downloadMassiveInpasExport(validations, year, month);
      setSuccess(`Esportazione INPAS completata: ${validations.length} cartellini esportati.`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore durante l\'esportazione');
      console.error('Errore esportazione:', err);
    } finally {
      setExportingInpas(false);
    }
  };

  const handleExportStandard = async () => {
    try {
      setExportingStandard(true);
      setError('');
      setSuccess('');

      const validations = await loadRegularValidations();

      if (validations.length === 0) {
        setError('Nessun cartellino regolare trovato per il periodo selezionato');
        return;
      }

      const result = await downloadMassiveStandardExport(
        validations,
        year,
        month,
        async (validation, startDate, endDate) => checkoutAdminAPI.getUserHistory(validation.user_id, startDate, endDate)
      );

      if (result.failureCount > 0) {
        const failedPreview = result.failedEmployees.slice(0, 3).join(', ');
        const failedSuffix = result.failedEmployees.length > 3 ? ', ...' : '';
        setError(
          `Export standard completato con errori: ${result.successCount} cartellini generati, ${result.failureCount} non esportati` +
          (failedPreview ? ` (${failedPreview}${failedSuffix})` : '')
        );
      } else {
        setSuccess(`Esportazione standard completata: ${result.successCount} cartellini esportati.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore durante l\'esportazione standard');
      console.error('Errore esportazione standard:', err);
    } finally {
      setExportingStandard(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <AssessmentIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Esportazione Massiva Cartellini Regolari
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" mb={2}>
          Esporta tutti i cartellini dei dipendenti regolari (senza anomalie) per un mese specifico.
          Sono disponibili sia il formato INPAS sia il formato standard gia usato nel cartellino gruppi.
        </Typography>

        {isGroupAdmin && !isSuperAdmin && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Nota:</strong> Come admin di gruppo, vedrai e potrai esportare solo i cartellini dei dipendenti assegnati al tuo gruppo.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="year-label">Anno</InputLabel>
              <Select
                labelId="year-label"
                value={year}
                label="Anno"
                onChange={(e: SelectChangeEvent<number>) => setYear(e.target.value as number)}
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const currentYear = new Date().getFullYear() - i;
                  return (
                    <MenuItem key={currentYear} value={currentYear}>
                      {currentYear}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="month-label">Mese</InputLabel>
              <Select
                labelId="month-label"
                value={month}
                label="Mese"
                onChange={(e: SelectChangeEvent<number>) => setMonth(e.target.value as number)}
              >
                {inpasMonthNames.map((name, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {stats && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Statistiche
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Totale dipendenti validati: <strong>{stats.total}</strong>
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      Dipendenti regolari: <strong>{stats.regular}</strong>
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      Dipendenti con anomalie: <strong>{stats.withAnomalies}</strong>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={exportingInpas ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={handleExportInpas}
                  disabled={exportingInpas || exportingStandard || loading}
                  fullWidth
                  sx={{ py: 1.5 }}
                >
                  {exportingInpas ? 'Esportazione INPAS...' : 'Esporta Cartellini Regolari INPAS'}
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  startIcon={exportingStandard ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={handleExportStandard}
                  disabled={exportingStandard || exportingInpas || loading}
                  fullWidth
                  sx={{ py: 1.5 }}
                >
                  {exportingStandard ? 'Esportazione standard...' : 'Esporta Cartellini Regolari Standard'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {validatedEmployees.length > 0 && (
        <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
          <Box display="flex" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Dipendenti Validati
            </Typography>
            <Box
              sx={{
                ml: 2,
                px: 2,
                py: 0.5,
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 'bold',
              }}
            >
              {validatedEmployees.length}
            </Box>
            <Box
              sx={{
                ml: 'auto',
                px: 2,
                py: 0.5,
                bgcolor: 'info.main',
                color: 'white',
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 'bold',
              }}
            >
              {inpasMonthNames[month - 1]} {year}
            </Box>
          </Box>

          <TableContainer
            sx={{
              maxHeight: '500px',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Dipendente
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Matricola
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Codice Fiscale
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Validato il
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }} align="center">
                    Stato
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {validatedEmployees.map((validation) => (
                  <TableRow
                    key={validation.id}
                    sx={{
                      '&:hover': {
                        bgcolor: 'success.light',
                      },
                      bgcolor: 'rgba(76, 175, 80, 0.05)',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {validation.employee_surname} {validation.employee_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {validation.employee_matricola || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {validation.employee_codice_fiscale || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(validation.validated_at).toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label="Validato"
                        size="small"
                        color="success"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default TimesheetsMassExport;
